/**
 * Tallents Payment Form
 * @version 1.0
 * @author Bikash Limbu
 * @copyright Kuki Ventures Ltd 2020
 */

import FormValidation from "./FormValidation";

const PayInvoice = (() => {
    let options;
    const DOM = {};

    const cacheDOM = () => {
        const { credit_cards } = options;
        DOM.invoiceForm = document.getElementById('worldpay' );
        DOM.invoiceFormContainer = document.getElementsByClassName('js-pay-invoice-form' )[0];
        DOM.numberOfCreditCards = credit_cards.length;
        DOM.paymentType = document.getElementById('ph-paymentType');
        // DOM.paymentReason = document.getElementById('paymentReason');
        DOM.paymentReason = document.getElementsByClassName('dropdown-payment__button')[0];
        DOM.invoiceAmount = document.getElementById('invoice_amount');
        DOM.paymentAmount = document.getElementById('amount');
        DOM.description = document.getElementById('description');
        DOM.submitButton = document.querySelector('input[type="submit"]');
        if ( DOM.invoiceFormContainer ) {
            DOM.requiredElements = DOM.invoiceFormContainer.querySelectorAll('[data-validation="required"]');
        }
        DOM.dropdownButtonSelect = document.getElementsByClassName("dropdown-payment__button")[0];
        DOM.formLoader =  document.getElementsByClassName("js-loader")[0];
    }

    /**
     * Gets available payment types based on reason of payment
     * @param paymentReason
     * @returns card_types || new cards array
     */
    const getPaymentTypes = ( paymentReason ) => {
        const { card_types, credit_cards } = options;

        if ( 'Deposit' === paymentReason ) {
            return Object.fromEntries(
                Object.entries(card_types).filter(
                    ([key]) => !credit_cards.includes(key)
                )
            );
        }

        return card_types;
    }

    /**
     * Checks if payment type is a credit card
     * @param paymentType
     * @returns {boolean}
     */
    const isCreditCard = (paymentType) => {
        const { credit_cards } = options;
        return credit_cards.includes(paymentType);
    }

    /**
     * Check for valid card type
     * @param paymentType
     * @returns {boolean}
     */
    const isValidCardType = ( paymentType ) => {
        const { card_types } = options;
        return Object.keys(card_types).includes(paymentType);
    }

    /**
     * Populates card types in, based on payment reason dropdown selection
     * @param paymentReason
     */
    const populateCardTypes = ( paymentReason ) => {
        const { payment_type_msg } = options;

        while (DOM.paymentType.firstChild) {
            DOM.paymentType.removeChild(DOM.paymentType.firstChild)
        }

        if (! paymentReason) {
            const paymentTypeMessage = document.createElement('p');
            paymentTypeMessage.insertAdjacentHTML('afterbegin', payment_type_msg);
            DOM.paymentType.appendChild(paymentTypeMessage);
            return;
        }

        const paymentTypeOptionsPhEl = document.createElement('div');
        paymentTypeOptionsPhEl.setAttribute('id', 'paymentTypeOptionsPhEl');

        DOM.paymentType.insertAdjacentHTML('afterbegin', '<label for="paymentType">Payment Type *</label>');
        DOM.paymentType.appendChild(paymentTypeOptionsPhEl);

        const cardTypes = getPaymentTypes(paymentReason);
        Object.entries(cardTypes).forEach((key, value) => {
            const elKey = 'paymentType-' + key[0];
            const paymentTypeOptionsPhElWrapper = document.createElement('div');
            paymentTypeOptionsPhElWrapper.classList.add('paymentTypeOptionsPhElWrapper');
            paymentTypeOptionsPhElWrapper.insertAdjacentHTML('afterbegin', `<input type="radio" id="${elKey}" name="paymentType"  data-validation="required" required value="${key[0]}"/>`);
            paymentTypeOptionsPhElWrapper.insertAdjacentHTML('afterbegin', `<label for="${elKey}" id="${elKey}-label">${key[1]}</label>`);
            paymentTypeOptionsPhEl.appendChild(paymentTypeOptionsPhElWrapper);
        });

        DOM.paymentType.insertAdjacentHTML('beforeend', '<p class="paymentNotice"><small>Please ensure you select the correct payment type before pressing submit.</small></p>');
    }

    /**
     * Display credit card surcharge fee notice when credit cards are available
     * for selection
     */
    const creditCardCharge = () => {
        const { credit_cards, cc_surcharge, cc_surcharge_msg } = options;
        const selectedCardTypes = getPaymentTypes(DOM.paymentReason.getAttribute('data-value'));
        const creditCards = Object.fromEntries(
            Object.entries(selectedCardTypes).filter(
                ([key]) => credit_cards.includes(key)
            )
        );

        if ( Object.keys(creditCards).length > 0 ) {
            const surchargeInPercent = cc_surcharge * 100;
            const surchargeNotice = cc_surcharge_msg.replace('%s', surchargeInPercent);
            DOM.paymentType.insertAdjacentHTML('afterend', `<p class="paymentNotice"><small>${surchargeNotice}</small></p>`);
        }
    }

    /**
     * Updated the card type options for selection
     */
    const updateCardTypes = () => {
        // Updates card types
        populateCardTypes(DOM.paymentReason.getAttribute('data-value'));

        // Enable this to display credit card surcharge notice when credit card is available to select
        // creditCardCharge();
    }

    /**
     * Initial adding of card types
     */
    const initialiseCardTypes = () => {
        // Pre-populates payment types
        populateCardTypes(DOM.paymentReason.getAttribute('data-value'));
    }

    /**
     * Perform validation and submit the payment form
     * @param e
     */
    const submitPaymentForm = (e) => {
        e.preventDefault();
        // Disable form for the user
        DOM.invoiceForm.classList.add('js-loading');
        DOM.formLoader.classList.add('active');

        // Perform form validation
        FormValidation.formValidation(DOM.requiredElements, DOM.dropdownButtonSelect);

        // Check at least one card option is selected before form submission
        const cardOptionSelected = validateCardSelection();

        // Gather form details for submission only when there is no validation error
        if (!FormValidation.validationError && cardOptionSelected) {
            const paymentTypeOptions = document.getElementsByName('paymentType');
            const paymentTypeSelected = Array.from(paymentTypeOptions).filter(paymentOption => paymentOption.checked)[0];
            const paymentType = paymentTypeSelected.getAttribute('value');

            const invoiceAmount = DOM.invoiceAmount.value.replace(/[^0-9.,]/g, '')
            DOM.invoiceAmount.setAttribute('value', invoiceAmount);

            const total = parseFloat(invoiceAmount);
            if (isValidCardType(paymentType)) {
                DOM.paymentAmount.setAttribute('value', total.toString());
                DOM.description.setAttribute('value', DOM.paymentReason.getAttribute('data-value'));
            }
            DOM.invoiceForm.submit();
        } else {
            // Enable form to fix the validation error
            DOM.invoiceForm.classList.remove('js-loading');
            DOM.formLoader.classList.remove('active');
        }
    }

    /**
     * Check if at least one card has been selected by the user from the
     * available options
     * @returns {boolean}
     */
    const validateCardSelection = () => {
        const cardOptionsContainer = document.getElementById('paymentTypeOptionsPhEl');
        // Ignore the card validation if the cards are not displayed yet on the screen
        if (!cardOptionsContainer) {
            return false;
        }

        const cardOptions = document.querySelectorAll('input[name="paymentType"]');
        const cardSelected = Array.from(cardOptions).filter(card => card.checked).length > 0;

        if ( !cardSelected ) {
            FormValidation.showValidationsError( cardOptionsContainer, 'Please select a card from the above.');
            return false;
        }

        return cardSelected;
    }

    /**
     * Mutation observer listening for change in attributes
     * @param target
     * @param callback
     */
    const listenForSelection = (target, callback) => {
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type === "attributes") {
                    callback(mutation.target);
                }
            });
        });

        const config = { attributes: true, childList: false, subtree: false };

        observer.observe(target, config);
    };

    /**
     * Event Listeners for elements
     */
    const eventListeners = () => {
        Array.from(DOM.requiredElements).forEach(element => {
            const requiredElement = element.tagName === 'SELECT' ? DOM.dropdownButtonSelect : element;
            requiredElement.addEventListener('focusout', function() {
                FormValidation.validateFormFields(requiredElement);
            });
            requiredElement.addEventListener('change', function() {
                FormValidation.validateFormFields(requiredElement);
            });
        })
        listenForSelection(DOM.paymentReason, updateCardTypes);
        listenForSelection(DOM.dropdownButtonSelect, FormValidation.validateFormFields);
        DOM.submitButton.addEventListener('click', submitPaymentForm)
    }

    const init = (customOptions) => {
        const defaultOptions = {
            card_types: {
                VISA: 'Visa Credit',
                VISD: 'Visa Debit',
                VIED: 'Visa Electron',
                MSCD: 'MasterCard Credit',
                DMC:  'MasterCard Debit',
                MAES: 'Maestro'
            },
            credit_cards: ['VISA', 'MSCD'],
            cc_surcharge: 0.01,
            cc_surcharge_msg: 'If payment is made by credit card %s% is to be added.',
            payment_type_msg: '&#9432; Select the "Reason For Payment" to show the Payment Type'
        };

        options = Object.assign(defaultOptions, customOptions)

        cacheDOM();

        if ( DOM.invoiceForm ) {
            initialiseCardTypes();
            eventListeners();
        }
    }

    return {
        init
    }

})();

export default PayInvoice;
